import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';

type UserRef = {
  id: number;
  email?: string;
};

type ApprovalStatusRef = {
  id: number;
  title?: string;
};

type EscalationTypeRef = {
  id: number;
  name?: string;
};

type ProductRef = {
  id: number;
  name?: string;
};

type OpportunityRef = {
  id: string;
  customer_id?: string | null;
};

type EscalationRef = {
  id?: number;
  product?: ProductRef | null;
  escalation_type?: EscalationTypeRef | null;
  status?: ApprovalStatusRef | null;
  requested_by?: UserRef | null;
  priority?: string | null;
  desired_completion_date?: string | null;
  opportunity?: OpportunityRef | null;
  description?: string | null;
  stage?: number | null;
};

type AssignmentRef = {
  id: number;
  assignment_type?: string | null;
  status?: string | null;
  assigned_to?: UserRef | null;
};

type ApprovalRef = {
  id: number;
  approval_request_id?: number | null;
  status?: string | null;
  approver?: UserRef | null;
  target_assignment_type?: string | null;
  level?: number | null;
};

export type EscalationHistoryRecord = {
  id?: number;
  event_type: string;
  previous_value?: string | null;
  new_value?: string | null;
  notes?: string | null;
  created?: string;
  performed_by?: UserRef | null;
  escalation?: EscalationRef | null;
  assignment?: AssignmentRef | null;
  approval?: ApprovalRef | null;
};

interface Props {
  open: boolean;
  editItem: EscalationHistoryRecord | null;
  onClose: () => void;
  readOnly?: boolean;
}

const formatEventType = (value?: string): string => {
  if (!value) return '';
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatDateTime = (value?: string): string => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const EscalationHistoryViewDialog: React.FC<Props> = ({
                                                        open,
                                                        editItem,
                                                        onClose,
                                                      }) => {
  if (!editItem) return null;

  const escalation = editItem.escalation ?? null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Escalation History Details</DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'grid', gap: 1.25 }}>
          <Typography variant="body2">
            <strong>History ID:</strong> {editItem.id ?? ''}
          </Typography>
          <Typography variant="body2">
            <strong>Escalation ID:</strong> {editItem.escalation?.id ?? ''}
          </Typography>
          <Typography variant="body2">
            <strong>Event Type:</strong> {formatEventType(editItem.event_type)}
          </Typography>
          <Typography variant="body2">
            <strong>Created:</strong> {formatDateTime(editItem.created)}
          </Typography>
          <Typography variant="body2">
            <strong>Performed By:</strong> {editItem.performed_by?.email ?? ''}
          </Typography>
          <Typography variant="body2">
            <strong>Previous Value:</strong> {editItem.previous_value ?? ''}
          </Typography>
          <Typography variant="body2">
            <strong>New Value:</strong> {editItem.new_value ?? ''}
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Typography variant="body2">
            <strong>Notes:</strong>
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {editItem.notes ?? ''}
          </Typography>

          {escalation && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1">Escalation Details</Typography>

              <Typography variant="body2">
                <strong>Product:</strong> {escalation.product?.name ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Escalation Type:</strong> {escalation.escalation_type?.name ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {escalation.status?.title ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Requested By:</strong> {escalation.requested_by?.email ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Priority:</strong> {escalation.priority ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Desired Completion:</strong> {escalation.desired_completion_date ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Opportunity:</strong> {escalation.opportunity?.id ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Stage:</strong> {escalation.stage ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Description:</strong>
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {escalation.description ?? ''}
              </Typography>
            </>
          )}

          {(editItem.assignment || editItem.approval) && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1">Related Objects</Typography>
            </>
          )}

          {editItem.assignment && (
            <Box>
              <Typography variant="body2">
                <strong>Assignment ID:</strong> {editItem.assignment.id}
              </Typography>
              <Typography variant="body2">
                <strong>Assignment Type:</strong> {editItem.assignment.assignment_type ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Assignment Status:</strong> {editItem.assignment.status ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Assigned To:</strong> {editItem.assignment.assigned_to?.email ?? ''}
              </Typography>
            </Box>
          )}

          {editItem.approval && (
            <Box>
              <Typography variant="body2">
                <strong>Approval ID:</strong> {editItem.approval.id}
              </Typography>
              <Typography variant="body2">
                <strong>Approval Request ID:</strong> {editItem.approval.approval_request_id ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Approval Status:</strong> {editItem.approval.status ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Approver:</strong> {editItem.approval.approver?.email ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Target Assignment Type:</strong> {editItem.approval.target_assignment_type ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Level:</strong> {editItem.approval.level ?? ''}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EscalationHistoryViewDialog;